import React, { useState } from 'react';
import { ArrowLeft, Package, Calendar, CheckCircle, AlertTriangle, Clock, ChevronLeft, ChevronRight, HelpCircle, Wine, FileText } from 'lucide-react';
import { Header } from '../components/Header';
import { BOMDetailsCard } from '../components/BOMDetailsCard';
import { LabelComplianceCard } from '../components/LabelComplianceCard';
import { LabelPreviewCard } from '../components/LabelPreviewCard';
import { BOMVersionHistory } from '../components/BOMVersionHistory';
import { EditLabelModal } from '../components/EditLabelModal';
type RiskLevel = 'none' | 'low' | 'medium' | 'high';
export function ProductDetail() {
  const [selectedBOMVersion, setSelectedBOMVersion] = useState('v2.1');
  const [activeTab, setActiveTab] = useState('bom'); // Default to 'bom' instead of 'claims'
  const [isEditLabelModalOpen, setIsEditLabelModalOpen] = useState(false);
  const [caseSkuPage, setCaseSkuPage] = useState(0);
  const product = {
    displayName: 'Estate Reserve Cabernet Sauvignon 2019',
    sku: '0012345678901234',
    slug: 'CAB-RES-750',
    brand: 'Sonoma Valley Vineyards',
    manufacturer: 'Sonoma Valley Winery',
    listPrice: 42.99,
    category: 'Red Wine',
    status: 'validated',
    lastUpdated: '2 hours ago',
    riskLevel: 'low' as RiskLevel,
    riskReason: 'All components verified with proper certification',
    image: 'https://images.unsplash.com/photo-1586370434639-0fe43b2d32e6?w=120&h=120&fit=crop&crop=center',
    associatedCaseSkus: [{
      sku: '0112345678901234',
      displayName: 'Estate Reserve Cabernet Sauvignon 2019 - 6 Bottle Case',
      itemQuantity: 6
    }, {
      sku: '0212345678901234',
      displayName: 'Estate Reserve Cabernet Sauvignon 2019 - 12 Bottle Case',
      itemQuantity: 12
    }, {
      sku: '0312345678901234',
      displayName: 'Estate Reserve Cabernet Sauvignon 2019 - Gift Set (2 Bottles)',
      itemQuantity: 2
    }, {
      sku: '0412345678901234',
      displayName: 'Estate Reserve Cabernet Sauvignon 2019 - Magnum (1.5L)',
      itemQuantity: 1
    }]
  };
  const CASES_PER_PAGE = 2;
  const totalPages = Math.ceil(product.associatedCaseSkus.length / CASES_PER_PAGE);
  const visibleCases = product.associatedCaseSkus.slice(caseSkuPage * CASES_PER_PAGE, (caseSkuPage + 1) * CASES_PER_PAGE);
  const nextPage = () => {
    if (caseSkuPage < totalPages - 1) {
      setCaseSkuPage(prev => prev + 1);
    }
  };
  const prevPage = () => {
    if (caseSkuPage > 0) {
      setCaseSkuPage(prev => prev - 1);
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'validated':
        return <CheckCircle className="w-5 h-5 text-green-600" data-id="element-399" />;
      case 'issues':
        return <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-400" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" data-id="element-401" />;
      default:
        return null;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'validated':
        return 'TTB Label Approved';
      case 'issues':
        return 'Label Issues Detected';
      case 'pending':
        return 'TTB Review Pending';
      default:
        return status;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'validated':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'issues':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getRiskColor = (level: RiskLevel) => {
    switch (level) {
      case 'high':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'medium':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'none':
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };
  const getRiskLabel = (score: number) => {
    if (score >= 70) return 'High Risk';
    if (score >= 30) return 'Medium Risk';
    return 'Low Risk';
  };
  const renderTabContent = () => {
    if (activeTab === 'bom') {
      return <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-402">
          <div className="lg:col-span-3 space-y-6" data-id="element-403">
            <div className="bg-white rounded-lg border" data-id="element-404">
              <div className="p-6 border-b border-gray-200" data-id="element-405">
                <div className="flex items-center space-x-2" data-id="element-406">
                  <Wine className="w-5 h-5 text-gray-600" data-id="element-407" />
                  <div data-id="element-408">
                    <h2 className="text-lg font-semibold text-gray-900" data-id="element-409">
                      Bill of Materials (BOM)
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-410">
                      Wine composition, sourcing, and packaging details
                    </p>
                  </div>
                </div>
              </div>
              <BOMDetailsCard data-id="element-411" />
            </div>
          </div>
          <div className="space-y-6" data-id="element-412">
            <BOMVersionHistory selectedVersion={selectedBOMVersion} data-id="element-413" />
          </div>
        </div>;
    }
    return <div className="space-y-6" data-id="element-414">
        <LabelPreviewCard data-id="element-415" />
        <LabelComplianceCard onEditLabel={() => setIsEditLabelModalOpen(true)} data-id="element-416" />
        <EditLabelModal isOpen={isEditLabelModalOpen} onClose={() => setIsEditLabelModalOpen(false)} productSku={product.sku} productName={product.displayName} data-id="element-417" />
      </div>;
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-418">
      <Header data-id="element-419" />
      <div className="bg-white border-b border-gray-200" data-id="element-420">
        <div className="max-w-7xl mx-auto px-6 py-6" data-id="element-421">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center space-x-2 text-sm mb-6" data-id="element-422">
            <a href="#" className="text-gray-600 hover:text-gray-900" data-id="element-423">
              Products
            </a>
            <span className="text-gray-400" data-id="element-424">/</span>
            <span className="text-gray-900 font-medium" data-id="element-425">{product.sku}</span>
            <span className="text-gray-400" data-id="element-426">/</span>
            <span className="text-gray-600" data-id="element-427">{product.displayName}</span>
          </nav>
          <div className="flex items-start justify-between" data-id="element-428">
            <div className="flex items-start space-x-4" data-id="element-429">
              <img src={product.image} alt={product.displayName} className="w-16 h-16 rounded-lg object-cover" data-id="element-430" />
              <div data-id="element-431">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-432">
                  {product.displayName}
                </h1>
                <div className="flex flex-col space-y-2 mt-2" data-id="element-433">
                  <div className="flex items-center space-x-4" data-id="element-434">
                    <span className="text-sm font-medium text-gray-700" data-id="element-435">
                      SKU:
                    </span>
                    <span className="text-sm text-gray-900" data-id="element-436">{product.sku}</span>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-437">
                    <span className="text-sm font-medium text-gray-700" data-id="element-438">
                      Manufacturer:
                    </span>
                    <span className="text-sm text-gray-900" data-id="element-439">
                      {product.manufacturer}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-440">
                    <span className="text-sm font-medium text-gray-700" data-id="element-441">
                      List Price:
                    </span>
                    <span className="text-sm text-gray-900" data-id="element-442">
                      ${product.listPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Risk Score */}
            <div className="flex flex-col items-end space-y-4" data-id="element-443">
              <div className="relative" data-id="element-444">
                <div className={`flex flex-col items-center p-4 rounded-lg border ${getRiskColor(product.riskLevel)}`} data-id="element-445">
                  <div className="text-lg font-bold capitalize mb-1" data-id="element-446">
                    {product.riskLevel} Risk
                  </div>
                  <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700" title={product.riskReason} data-id="element-447">
                    <HelpCircle className="w-4 h-4" data-id="element-448" />
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2" data-id="element-449">
                <label className="text-sm font-medium text-gray-700" data-id="element-450">
                  BOM Version:
                </label>
                <select value={selectedBOMVersion} onChange={e => setSelectedBOMVersion(e.target.value)} className="px-3 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-451">
                  <option value="v2.1" data-id="element-452">v2.1 (Active)</option>
                  <option value="v2.0" data-id="element-453">v2.0</option>
                  <option value="v1.3" data-id="element-454">v1.3</option>
                </select>
              </div>
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${getStatusColor(product.status)}`} data-id="element-455">
                {getStatusIcon(product.status)}
                <span className="font-medium" data-id="element-456">
                  {getStatusText(product.status)}
                </span>
              </div>
            </div>
          </div>
          {/* Associated Case SKUs */}
          <div className="mt-6 border-t border-gray-200 pt-6" data-id="element-457">
            <div className="flex items-center justify-between mb-3" data-id="element-458">
              <h2 className="text-sm font-medium text-gray-700" data-id="element-459">
                Associated Case Configurations
              </h2>
              <span className="text-sm text-gray-500" data-id="element-460">
                {product.associatedCaseSkus.length} configurations
              </span>
            </div>
            <div className="relative" data-id="element-461">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-462">
                {visibleCases.map(caseSku => <div key={caseSku.sku} className="border border-gray-200 rounded-lg p-4 hover:border-purple-200 hover:bg-purple-50 transition-colors cursor-pointer" data-id="element-463">
                    <div className="flex flex-col space-y-2" data-id="element-464">
                      <span className="font-medium text-gray-900" data-id="element-465">
                        {caseSku.displayName}
                      </span>
                      <div className="flex items-center space-x-2" data-id="element-466">
                        <span className="text-sm text-gray-600" data-id="element-467">SKU:</span>
                        <span className="text-sm text-gray-900" data-id="element-468">
                          {caseSku.sku}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2" data-id="element-469">
                        <span className="text-sm text-purple-600 font-medium" data-id="element-470">
                          Contains {caseSku.itemQuantity} Item Units
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>
              {caseSkuPage > 0 && <button onClick={prevPage} className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 p-1 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50" data-id="element-471">
                  <ChevronLeft className="w-5 h-5 text-gray-600" data-id="element-472" />
                </button>}
              {caseSkuPage < totalPages - 1 && <button onClick={nextPage} className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 p-1 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50" data-id="element-473">
                  <ChevronRight className="w-5 h-5 text-gray-600" data-id="element-474" />
                </button>}
            </div>
          </div>
          <div className="flex items-center space-x-2 mt-4 text-sm text-gray-500" data-id="element-475">
            <Calendar className="w-4 h-4" data-id="element-476" />
            <span data-id="element-477">Last updated {product.lastUpdated}</span>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-478">
        <div className="max-w-7xl mx-auto px-6" data-id="element-479">
          <nav className="-mb-px flex space-x-8" data-id="element-480">
            <button onClick={() => setActiveTab('label')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'label' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-481">
              Product Label
            </button>
            <button onClick={() => setActiveTab('bom')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'bom' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-482">
              Bill of Materials
            </button>
          </nav>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-6 py-6" data-id="element-483">{renderTabContent()}</main>
    </div>;
}